import { Box, Card, CardBody, Flex, IconButton, SimpleGrid, Text } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAccountManager, useAccounts } from "applesauce-react/hooks";
import { CloseIcon } from "@chakra-ui/icons";

import Key01 from "../../components/icons/key-01";
import UserPlus01 from "../../components/icons/user-plus-01";
import UserAvatar from "../../components/user/user-avatar";
import UserName from "../../components/user/user-name";
import AccountTypeBadge from "../../components/accounts/account-info-badge";
import PasskeyUnlockButton from "../../components/passkey/passkey-unlock-button";

/** A clickable card option that renders as a router link */
function OptionCardLink({
  icon,
  title,
  description,
  to,
  state,
  colorScheme,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  to: string;
  state?: unknown;
  colorScheme?: string;
}) {
  return (
    <Card
      as={RouterLink}
      to={to}
      state={state}
      variant="outline"
      cursor="pointer"
      textAlign="left"
      w="full"
      _hover={{ borderColor: colorScheme ? `${colorScheme}.400` : "primary.400", shadow: "md" }}
      transition="all 0.15s"
      textDecoration="none !important"
    >
      <CardBody p="4">
        <Flex align="center" gap="2" mb="1">
          <Box color={colorScheme ? `${colorScheme}.400` : "primary.400"} flexShrink={0}>
            {icon}
          </Box>
          <Text fontWeight="bold" fontSize="md">
            {title}
          </Text>
        </Flex>
        <Text fontSize="sm" opacity={0.7}>
          {description}
        </Text>
      </CardBody>
    </Card>
  );
}

export default function SigninStartView() {
  const location = useLocation();
  const manager = useAccountManager();
  const accounts = useAccounts();

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap="3" w="full">
        <PasskeyUnlockButton />
        <OptionCardLink
          icon={<Key01 boxSize={6} />}
          title="Private Key"
          description="Sign in with your nsec private key."
          to="./nsec"
          state={location.state}
          colorScheme="primary"
        />
        <OptionCardLink
          icon={<UserPlus01 boxSize={6} />}
          title="Create Account"
          description="Generate a new key and get started."
          to="/join"
          colorScheme="green"
        />
      </SimpleGrid>

      {accounts.length > 0 && (
        <>
          <Text fontWeight="bold" mt="4">
            Existing accounts
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3" w="full">
            {accounts.map((account) => (
              <Card
                key={account.id}
                as="button"
                variant="outline"
                onClick={() => manager.setActive(account)}
                cursor="pointer"
                textAlign="left"
                w="full"
                _hover={{ borderColor: "primary.400", shadow: "md" }}
                transition="all 0.15s"
              >
                <CardBody p="4">
                  <Flex align="center" gap="2" mb="1">
                    <UserAvatar pubkey={account.pubkey} size="sm" flexShrink={0} />
                    <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                      <UserName pubkey={account.pubkey} />
                    </Text>
                    <IconButton
                      ms="auto"
                      aria-label="Remove account"
                      icon={<CloseIcon boxSize={3} />}
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                      flexShrink={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        manager.removeAccount(account);
                      }}
                    />
                  </Flex>
                  <AccountTypeBadge account={account} />
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </>
      )}
    </>
  );
}
